/* eslint-disable max-statements */
import { Box, Menu, MenuButton, MenuList, Portal } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BlueprintInstanceType, type ReferenceMap } from '@rta/rta-blueprint-library';
import { type ComponentController as BpComponentController, type ContextData, type ContextMenuList, DisplayMode, getContextMenuList, renderMenu } from '@rta/rta-blueprint-component';
import { Kitchen3dController as TdComponentController } from '@rta/rta-blueprint-3d-component';

import { resolveThreeDHit, threeDMenuController, type ThreeDNavigationOverride } from './helpers';

type ThreeDHit = {
	id: string;
	type: string;
	islandId: string | null;
	localPoint: { x: number; y: number; z: number } | null;
};

type ThreeDContextMenuProps = {
	tdController: TdComponentController | null;
	bpController: BpComponentController | null;
	children: React.ReactElement;
};

type OpenMenu = {
	context: ContextData;
	refMap: ReferenceMap;
	/** Offset from the clicked object's centre, in the 2D view's inches-and-y-down convention */
	position: { x: number; y: number };
	controller: BpComponentController;
	menuList: Array<ContextMenuList>;
};

/** What the 3D calls a hit object, in the blueprint's vocabulary. */
const MENU_KEY_BY_TYPE: Record<string, BlueprintInstanceType> = {
	appliance: BlueprintInstanceType.PanelItem,
	countertop: BlueprintInstanceType.Countertop,
	panel: BlueprintInstanceType.Panel,
	corner: BlueprintInstanceType.Panel
};

export type BuildFloorMenuParams = {
	bpController: BpComponentController;
	event: React.MouseEvent;
};

/**
 * The kitchen-wide workspace menu for a floor hit, built directly on the real (unproxied)
 * controller — workspace items are kitchen-scope and must not read a 3D navigation override.
 */
const buildFloorMenu = (params: BuildFloorMenuParams): OpenMenu => {
	const { bpController, event } = params;
	const context: ContextData = { menuKey: BlueprintInstanceType.NoOp, element: event, id: null, center: { x: 0, y: 0 } };
	const menuList = getContextMenuList({ data: { context, position: { x: 0, y: 0 }, relative: { x: 0, y: 0 } }, refMap: null, displayMode: bpController.navigation.displayMode, controller: bpController });
	// renderMenu declares refMap non-null but the 2D menu passes null the same way when a
	// right-click resolves to nothing in the blueprint; the menu items all guard on it.
	return { context, refMap: null as unknown as ReferenceMap, position: { x: 0, y: 0 }, controller: bpController, menuList };
};

/**
 * The 2D menus, raised from 3D hit tests. The actions run against the design's blueprint
 * controller, so they behave exactly as they do in the designer; only `navigation` is replaced,
 * by what was actually clicked in 3D, so an item never reads the 2D view's unrelated state.
 */
export const ThreeDContextMenu = (props: ThreeDContextMenuProps): React.ReactElement => {
	const { tdController, bpController, children } = props;
	const hitRef = useRef<ThreeDHit | null>(null);
	const [menu, setMenu] = useState<OpenMenu | null>(null);
	const [position, setPosition] = useState<[number, number]>([0, 0]);

	useEffect(() => {
		if (!tdController) return undefined;
		const subscriptionId = tdController.subscribe('object', 'right-click', (event: { payload?: Record<string, unknown> }) => {
			const payload = event?.payload ?? {};
			const data = (payload.data ?? {}) as Record<string, unknown>;
			hitRef.current = {
				id: String(payload.id ?? ''),
				type: String(payload.type ?? ''),
				islandId: (data.islandId as string) ?? null,
				localPoint: (data.localPoint as ThreeDHit['localPoint']) ?? null
			};
		});
		return () => {
			if (subscriptionId) tdController.unsubscribe(subscriptionId);
			hitRef.current = null;
		};
	}, [tdController]);

	const close = useCallback((): void => {
		setMenu(null);
		hitRef.current = null;
	}, []);

	const open = useCallback((event: React.MouseEvent): void => {
		event.preventDefault();
		const hit = hitRef.current;
		hitRef.current = null;

		// The floor has no blueprint id to resolve — it opens the kitchen-wide workspace menu.
		if (hit?.type === 'floor') {
			if (!bpController) {
				setMenu(null);
				return;
			}
			setPosition([event.clientX, event.clientY]);
			setMenu(buildFloorMenu({ bpController, event }));
			return;
		}

		// The engine's own contextmenu listener runs first and records the hit; nothing there
		// means the press landed on the sky or ended an orbit, and no menu belongs to it.
		if (!hit || !bpController || !hit.id) {
			setMenu(null);
			return;
		}

		const menuKey = MENU_KEY_BY_TYPE[hit.type];
		if (!menuKey) {
			setMenu(null);
			return;
		}

		const refMap = bpController.kitchen?.getReferenceMap(hit.id) ?? null;
		const island = refMap?.island ?? bpController.kitchen?.design?.islands?.findById(hit.islandId ?? '') ?? null;
		const { panel, position: panelPosition } = resolveThreeDHit({
			type: hit.type,
			island,
			knownPanel: refMap?.panel ?? null,
			islandPoint: hit.localPoint
		});

		// A merged surround mesh reports one panel's id for the whole island, so the menu's target
		// is the panel the geometry says was hit, not the id the scene happened to carry.
		const id = hit.type === 'panel' || hit.type === 'corner' ? panel?.id ?? hit.id : hit.id;
		const targetRefMap = id === hit.id ? refMap : bpController.kitchen?.getReferenceMap(id) ?? refMap;
		const context: ContextData = { menuKey, element: event, id, center: { x: 0, y: 0 } };
		const navigation: ThreeDNavigationOverride = {
			displayMode: DisplayMode.Panel,
			islandId: island?.id ?? null,
			// A countertop click is the plan view: the menus read a null panel as "on the countertop"
			panelId: menuKey === BlueprintInstanceType.Countertop ? null : panel?.id ?? null
		};

		const controller = threeDMenuController(bpController, navigation);
		const menuList = getContextMenuList({ data: { context, position: { x: 0, y: 0 }, relative: panelPosition }, refMap: targetRefMap, displayMode: navigation.displayMode, controller });

		setPosition([event.clientX, event.clientY]);
		// renderMenu declares refMap non-null but the 2D menu passes null the same way when a
		// right-click resolves to nothing in the blueprint; the menu items all guard on it.
		setMenu({ context, refMap: targetRefMap as ReferenceMap, position: panelPosition, controller, menuList });
	}, [bpController]);

	const enabled = Boolean(tdController && bpController && (bpController.options?.contextMenu ?? true));
	if (!enabled) return children;

	return (
		<Box display="contents" onContextMenu={open} onClick={close}>
			{children}

			<Portal>
				<Menu isLazy lazyBehavior="unmount" isOpen={menu !== null} gutter={0} onClose={close}>
					<MenuButton
						tabIndex={-1}
						w={1}
						h={1}
						style={{ position: 'absolute', left: position[0] - 5, top: position[1] - 15, cursor: 'default', pointerEvents: 'none' }}
					/>
					<MenuList>
						<Box position="relative" width="100%" height="100%" padding="2px 5px" onMouseLeave={close}>
							{menu && renderMenu({
								menuList: menu.menuList,
								state: {
									context: menu.context,
									position: menu.position,
									refMap: menu.refMap,
									controller: menu.controller
								}
							})}
						</Box>
					</MenuList>
				</Menu>
			</Portal>
		</Box>
	);
};
